
import { useEffect, useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  BlockNoteEditor, 
  PartialBlock
} from "@blocknote/core";
import { BlockNoteView, useBlockNote } from "@blocknote/react";
import "@blocknote/core/style.css";
import "./BlockNoteStyles.css";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  fetchDocumentation, 
  createDocumentation, 
  updateDocumentation 
} from "@/utils/services/documentationService";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { EmptyDocumentation } from "./EmptyDocumentation";
import { Eye, EyeOff } from "lucide-react";

interface ComponentDocumentationProps {
  componentId: string;
}

export function ComponentDocumentation({ componentId }: ComponentDocumentationProps) {
  const [title, setTitle] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [initialContent, setInitialContent] = useState<PartialBlock[]>([
    { type: "paragraph", content: "Comece a escrever sobre este componente..." }
  ]);
  const editorInitialized = useRef(false);
  
  const queryClient = useQueryClient();

  const { data: documentation, isLoading, isError } = useQuery({
    queryKey: ["component-documentation", componentId],
    queryFn: () => fetchDocumentation(componentId),
  });

  const createMutation = useMutation({
    mutationFn: ({ title, content }: { title: string; content: any }) => 
      createDocumentation(componentId, title, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["component-documentation", componentId] });
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, title, content, isPublished }: { id: string; title: string; content: any; isPublished: boolean }) => 
      updateDocumentation(id, title, content, isPublished),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["component-documentation", componentId] });
    }
  });

  const editor = useBlockNote({
    initialContent: initialContent,
    editable: isEditing,
    domAttributes: {
      editor: {
        class: "min-h-[200px] p-4 focus:outline-none"
      }
    }
  });

  useEffect(() => {
    if (documentation) {
      setTitle(documentation.title);
      setIsPublished(documentation.is_published);
      
      if (documentation.content && editor) {
        try {
          const contentToUse = Array.isArray(documentation.content) ? documentation.content : initialContent;
          setInitialContent(contentToUse);
          
          if (editor) {
            console.log("Carregando conteúdo no editor:", contentToUse);
            setTimeout(() => {
              editor.replaceBlocks(editor.topLevelBlocks, contentToUse);
            }, 100);
          }
        } catch (e) {
          console.error("Error setting editor content:", e);
        }
      }
    }
  }, [documentation, editor]);

  useEffect(() => {
    if (editor) {
      editor.isEditable = isEditing;
    }
  }, [isEditing, editor]);

  const handleSave = async () => {
    if (!title.trim()) {
      toast({
        title: "Erro",
        description: "O título é obrigatório",
        variant: "destructive",
      });
      return;
    }

    const content = editor ? editor.topLevelBlocks : [];
    console.log("Saving content:", content);
    
    if (documentation) {
      updateMutation.mutate({ 
        id: documentation.id, 
        title, 
        content, 
        isPublished
      });
    } else {
      createMutation.mutate({ title, content });
    }
  };

  const toggleEditMode = () => {
    if (isEditing) {
      handleSave();
    }
    setIsEditing(!isEditing);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <Skeleton className="h-8 w-3/4" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-red-500">Erro ao carregar a documentação.</p>
        </CardContent>
      </Card>
    );
  }

  if (!documentation && !isEditing) {
    return <EmptyDocumentation onStartEditing={() => setIsEditing(true)} />;
  }

  return (
    <Card className="border border-[#05445e]/20">
      <CardContent className="pt-6 space-y-4">
        <div className="flex flex-col md:flex-row justify-between gap-4 items-center">
          {isEditing ? (
            <Input 
              value={title} 
              onChange={(e) => setTitle(e.target.value)} 
              placeholder="Título da documentação"
              className="text-xl font-semibold mb-2 w-2/3"
            />
          ) : (
            <h3 className="text-2xl font-semibold">{title || "Sem título"}</h3>
          )}
          
          <div className="flex items-center space-x-4">
            {(documentation || isEditing) && (
              <div className="flex items-center space-x-2">
                <div className="space-x-2 flex items-center">
                  {isEditing && (
                    <Button 
                      onClick={handleSave} 
                      className="bg-[#189ab4] hover:bg-[#05445e]"
                      disabled={createMutation.isPending || updateMutation.isPending}
                    >
                      Salvar
                    </Button>
                  )}
                  <Button 
                    onClick={toggleEditMode}
                    className="bg-[#05445e] hover:bg-[#189ab4]"
                  >
                    Editar Documentação
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="documentation-editor border rounded-md p-2">
          <BlockNoteView
            editor={editor}
            theme="light"
          />
        </div>
        
        {documentation && (
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div className="flex items-center space-x-2">
              {isEditing && (
                <>
                  <Switch 
                    checked={isPublished} 
                    onCheckedChange={setIsPublished} 
                    id="published-footer"
                    disabled={!isEditing}
                  />
                  <Label htmlFor="published-footer" className="flex items-center space-x-1">
                    {isPublished ? (
                      <>
                        <Eye className="h-4 w-4" />
                        <span>Publicado</span>
                      </>
                    ) : (
                      <>
                        <EyeOff className="h-4 w-4" />
                        <span>Rascunho</span>
                      </>
                    )}
                  </Label>
                </>
              )}
            </div>
            <p>Versão: {documentation.version} • Atualizado em: {new Date(documentation.updated_at).toLocaleDateString()}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

